import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import { products } from "../../constants/products";
import { CheckoutService } from "../../services/CheckoutService";
import { useMatter } from "../../contexts/MatterContext";
import { Heading } from "../../design/heading";
import { Text } from "../../design/text";
import { Button } from "../../design/button";
import { Product } from "../../models/Product";

const ProductDetailPage: React.FC = () => {
  const { currentMatterId } = useMatter();
  const { productId } = useParams<{ productId: string }>();
  const selectedProduct = productId ? products[productId] || null : null;

  if (!selectedProduct) {
    return <ErrorView error={new Error("Product not found")} />;
  }

  const handleUnlock = async (product: Product) => {
    if (!currentMatterId) {
      return alert("Please select a matter first.");
    }
    try {
      const url = await CheckoutService.checkoutProduct(
        product,
        currentMatterId,
      );
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong. Please try again.");
    }
  };

  const upsellProducts = selectedProduct.upsellProducts?.map(
    (upsellId) => products[upsellId],
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <Breadcrumbs
        items={[
          { label: "Products", href: "/products" },
          { label: selectedProduct.title, href: `/product/${productId}` },
        ]}
      />

      <div className="flex flex-col gap-6 p-6 bg-gray-100 border shadow-md dark:bg-zinc-900 rounded-xl">
        <div className="flex flex-col gap-2">
          <Heading level={2}>{selectedProduct.title}</Heading>
          <Text>{selectedProduct.description}</Text>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="text-2xl font-bold">${selectedProduct.price}</div>
          <Button onClick={() => handleUnlock(selectedProduct)}>
            Unlock {selectedProduct.type === "bundle" ? "Bundle" : "Feature"}
          </Button>
        </div>
      </div>

      {upsellProducts && upsellProducts.length > 0 && (
        <div className="flex flex-col gap-4 mt-8">
          <Heading level={3}>Recommended for you</Heading>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {upsellProducts.map((upsell) => (
              <div
                key={upsell.id}
                className="flex flex-col gap-2 p-4 border rounded-lg shadow-md bg-gray-50 dark:bg-zinc-800"
              >
                <Heading level={4}>{upsell.title}</Heading>
                <Text>{upsell.description}</Text>
                <div className="flex items-center justify-between">
                  <div className="text-xl font-semibold">${upsell.price}</div>
                  <Button onClick={async () => handleUnlock(upsell)}>
                    Unlock
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
